import { useMemo } from 'react'

import { Box, Button, ButtonGroup, HStack, Text } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'

import CaseworkSortMenu from './CaseworkSortMenu'
import {
  activeStatuses,
  ARCHIVED_CASEWORK_TAB,
  districtOptions,
  filterableStatuses,
  inactiveStatuses,
  priorityOptions,
} from '../../../constants'
import { loadAgenciesData } from '../../../redux/features/agenciesSlice'
import {
  loadTopics,
  setCaseworkFilters,
} from '../../../redux/features/caseworkSlice'
import { loadContactMethods } from '../../../redux/features/contactMethodsSlice'
import {
  addLeadingZeroToDistrict,
  formatAgencyName,
  hasPermission,
} from '../../../utils'
import { AdvancedFilterInput, FilterInput, FilterMenuBase } from '../../ui'
import {
  ConstituentSelect,
  CaseworkTagSelect,
  StaffSelect,
  TopicSelect,
} from '../../ui/select'

const CaseworkFilterMenu = ({ isOpen, onClose, filterButtonRef }) => {
  const dispatch = useDispatch()

  const user = useSelector(state => state.auth.user)
  const tabIndex = useSelector(state => state.casework.tab_index)
  const agencyOptions = useSelector(state => state.agencies.data)
  const topics = useSelector(state => state.casework.topics) || []
  const allContactMethods = useSelector(
    state => state.contactmethods.data.results || []
  )

  const filters = useSelector(state => state.casework.filters) || []

  const selectedDistrict = !hasPermission(
    user,
    'directory.constituent.view_all_constituents'
  )
    ? [user.district]
    : (filters.district__in || []).map(district =>
        addLeadingZeroToDistrict(district)
      )

  const loadAgencies = () => {
    dispatch(loadAgenciesData())
  }

  const loadTopicCategories = () => {
    dispatch(loadTopics())
  }

  const loadContactMethodOptions = () => {
    dispatch(loadContactMethods())
  }

  const issueOptions = useMemo(
    () => topics?.map(topic => topic.issue).filter(issue => !!issue) || [],
    [topics]
  )

  const contactMethodsWithCasework = useMemo(() => {
    return allContactMethods.filter(
      cm => cm.is_active || (cm.casework_count && cm.casework_count > 0)
    )
  }, [allContactMethods])

  const contactMethodOptions = useMemo(() => {
    return [
      'Unassigned',
      ...contactMethodsWithCasework.map(cm => ({
        value: cm.name,
        label: cm.name,
        isInactive: !cm.is_active,
      })),
    ]
  }, [contactMethodsWithCasework])

  return (
    <FilterMenuBase
      heading='Casework Filters'
      onClose={onClose}
      isOpen={isOpen}
      buttonRef={filterButtonRef}
      store='casework'
      sortMenu={<CaseworkSortMenu />}
    >
      {tabIndex !== ARCHIVED_CASEWORK_TAB && (
        <FilterInput
          label='Status'
          filterKey='status__in'
          store='casework'
          placeholder='Filter by status'
          options={filterableStatuses}
          labelNode={
            <ButtonGroup variant='link' size='sm'>
              <Button
                onClick={() =>
                  dispatch(
                    setCaseworkFilters({
                      ...filters,
                      status__in: [
                        ...new Set([
                          ...(filters.status__in || []),
                          ...activeStatuses,
                        ]),
                      ],
                    })
                  )
                }
              >
                Active
              </Button>
              <Button
                colorScheme='gray'
                onClick={() =>
                  dispatch(
                    setCaseworkFilters({
                      ...filters,
                      status__in: [
                        ...new Set([
                          ...(filters.status__in || []),
                          ...inactiveStatuses,
                        ]),
                      ],
                    })
                  )
                }
              >
                Inactive
              </Button>
            </ButtonGroup>
          }
        />
      )}
      <FilterInput
        label='Priority'
        filterKey='priority__in'
        store='casework'
        placeholder='Filter by priority'
        options={priorityOptions}
      />
      <FilterInput
        label='Address'
        filterKey='address'
        store='casework'
        placeholder='Filter by address (city, state, zipcode, etc)'
      />
      <FilterInput
        label='Contact Method'
        filterKey='contact_methods'
        store='casework'
        placeholder='Filter by contact method'
        options={contactMethodOptions}
        loadOptions={loadContactMethodOptions}
      />

      <FilterInput
        label='City Agencies'
        filterKey='agencies'
        store='casework'
        placeholder='Filter by agency'
        options={Object.keys(agencyOptions)}
        formatOption={formatAgencyName}
        loadOptions={loadAgencies}
      />

      <FilterInput
        label='Topic Category'
        filterKey='issues'
        store='casework'
        placeholder='Filter by topic category'
        options={issueOptions}
        loadOptions={loadTopicCategories}
      />

      <Box>
        <Text fontSize='lg'>Topics</Text>
        <AdvancedFilterInput
          store='casework'
          setFilters={setCaseworkFilters}
          filterKey='topics'
          labelKey='name'
        >
          <TopicSelect
            showUnassigned
            inputProps={{
              id: 'topics-filter',
              placeholder: 'Filter by topic(s)',
            }}
          />
        </AdvancedFilterInput>
      </Box>
      <Box>
        <Text fontSize='lg'>Tags</Text>
        <AdvancedFilterInput
          store='casework'
          setFilters={setCaseworkFilters}
          filterKey='tags'
          labelKey='label'
        >
          <CaseworkTagSelect
            inputProps={{
              id: 'tags-filter',
              placeholder: 'Filter by tags(s)',
            }}
            preventCreate
          />
        </AdvancedFilterInput>
      </Box>
      <Box>
        <Text fontSize='lg'>Assigned Staff</Text>
        <AdvancedFilterInput
          store='casework'
          setFilters={setCaseworkFilters}
          filterKey='assigned_staff'
          labelKey='full_name'
        >
          <StaffSelect
            districtOnly={!hasPermission(user, 'casework.view_all_casework')}
            inputProps={{
              id: 'assigned-staff-filter',
              placeholder: 'Filter by assigned staff',
            }}
            showUnassigned
          />
        </AdvancedFilterInput>
      </Box>
      <Box>
        <Text fontSize='lg'>Intake By</Text>
        <AdvancedFilterInput
          store='casework'
          setFilters={setCaseworkFilters}
          filterKey='intake_staff'
          labelKey='full_name'
        >
          <StaffSelect
            districtOnly={!hasPermission(user, 'casework.view_all_casework')}
            inputProps={{
              id: 'intake-staff-filter',
              placeholder: 'Filter by Intake By',
            }}
          />
        </AdvancedFilterInput>
      </Box>
      <Box>
        <Text fontSize='lg'>Constituent</Text>
        <AdvancedFilterInput
          store='casework'
          setFilters={setCaseworkFilters}
          filterKey='constituents'
          labelKey='identifier'
        >
          <ConstituentSelect
            filters={{ district__in: selectedDistrict }}
            inputProps={{
              id: 'constituent-filter',
              placeholder: 'Filter by constituent(s)',
            }}
          />
        </AdvancedFilterInput>
      </Box>
      {hasPermission(user, 'casework.view_all_casework') && (
        <FilterInput
          label='District'
          filterKey='district__in'
          store='casework'
          placeholder='Filter by district'
          options={districtOptions}
        />
      )}
      <HStack>
        <Box w='100%'>
          <FilterInput
            label='Opened on or after'
            filterKey='opened_after'
            store='casework'
            inputType='date'
          />
        </Box>
        <Box w='100%'>
          <FilterInput
            label='Opened before'
            filterKey='opened_before'
            store='casework'
            inputType='date'
          />
        </Box>
      </HStack>
      <HStack>
        <Box w='100%'>
          <FilterInput
            label='Closed on or after'
            filterKey='closed_after'
            store='casework'
            inputType='date'
          />
        </Box>
        <Box w='100%'>
          <FilterInput
            label='Closed before'
            filterKey='closed_before'
            store='casework'
            inputType='date'
          />
        </Box>
      </HStack>
      <HStack>
        <Box w='100%'>
          <FilterInput
            label='Last updated on or after'
            filterKey='updated_after'
            store='casework'
            inputType='date'
          />
        </Box>
        <Box w='100%'>
          <FilterInput
            label='Last updated before'
            filterKey='updated_before'
            store='casework'
            inputType='date'
          />
        </Box>
      </HStack>
    </FilterMenuBase>
  )
}

export default CaseworkFilterMenu
